import React, { useState, useEffect, useRef, useMemo } from 'react';
import { subscribeToMessages, sendMessage, markMessagesRead } from '../../services/messagingService';
import toast from 'react-hot-toast';

// Friendly labels for content-filter blocks
const FILTER_LABELS = {
  phone_number: 'phone number',
  email: 'email address',
  alt_comms: 'reference to another messaging app',
  off_platform_payment: 'mention of off-platform payment',
  circumvention: 'attempt to communicate outside the platform',
};

export default function ChatWindow({ bookingId, userId, userRole, otherPartyName, booking }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  // Compute chat availability based on booking state
  // - Hidden entirely if cancelled or completed
  // - Disabled with explanation if pre-driver (pending_pool, pending_targeted)
  // - Disabled with countdown if confirmed and >30min until pickup
  // - Enabled if active trip (en_route, arrived, pin_verified, in_progress) OR confirmed within 30min
  const chatGate = useMemo(() => {
    if (!booking) return { hidden: false, disabled: true, reason: 'Loading...' };

    const status = booking.status;
    if (status === 'cancelled' || status === 'completed') {
      return { hidden: true };
    }
    if (status === 'pending_pool' || status === 'pending_targeted') {
      return { hidden: false, disabled: true, reason: 'Chat opens when a driver accepts your booking.' };
    }
    if (status === 'confirmed') {
      const pickupTime = booking.scheduledPickupTime?.toDate
        ? booking.scheduledPickupTime.toDate()
        : (booking.scheduledPickupTime ? new Date(booking.scheduledPickupTime) : null);
      if (pickupTime) {
        const minutesUntilPickup = (pickupTime.getTime() - Date.now()) / 60000;
        if (minutesUntilPickup > 30) {
          const hoursOrMin = minutesUntilPickup >= 60
            ? `${Math.floor(minutesUntilPickup / 60)}h ${Math.round(minutesUntilPickup % 60)}m`
            : `${Math.round(minutesUntilPickup)}min`;
          return {
            hidden: false,
            disabled: true,
            reason: `Chat opens 30 minutes before pickup (in ${hoursOrMin}).`,
          };
        }
      }
    }
    return { hidden: false, disabled: false };
  }, [booking]);

  useEffect(() => {
    if (chatGate.hidden) return;
    const unsub = subscribeToMessages(bookingId, (msgs) => {
      setMessages(msgs);
      markMessagesRead(bookingId, userId);
    });
    return unsub;
  }, [bookingId, userId, chatGate.hidden]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending || chatGate.disabled) return;
    setSending(true);
    try {
      const result = await sendMessage(bookingId, userId, userRole, newMsg.trim());
      if (result.blocked) {
        const label = FILTER_LABELS[result.label] || 'restricted content';
        toast.error(
          `Message blocked: detected ${label}. For your safety, contact info, off-platform payment, and other-app references aren't allowed in chat. This attempt has been logged.`,
          { duration: 6000 }
        );
        // Don't clear input — let user edit
      } else {
        setNewMsg('');
      }
    } catch (err) {
      toast.error('Send failed: ' + (err.message || 'unknown error'));
    }
    setSending(false);
  };

  // Hidden state: don't render anything
  if (chatGate.hidden) return null;

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: 500 }}>
      <div className="px-4 py-3 border-b border-slate-200 font-bold text-brand-dark">
        Chat with {otherPartyName}
      </div>
      <div className="flex-1 overflow-auto p-4 flex flex-col gap-2">
        {messages.length === 0 && !chatGate.disabled && (
          <div className="text-center text-muted text-[0.85rem] p-10">No messages yet</div>
        )}
        {messages.map((msg) => {
          const isMe = msg.senderId === userId;
          return (
            <div key={msg.id} className="px-3.5 py-2.5 rounded-2xl" style={{
              alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '75%',
              background: isMe ? '#2dd4a8' : '#f1f5f9', color: isMe ? '#fff' : '#0a1628',
              borderBottomRightRadius: isMe ? 4 : 16, borderBottomLeftRadius: isMe ? 16 : 4,
            }}>
              <div className="text-[0.9rem]">{msg.text}</div>
              <div className="text-[0.7rem] mt-1 text-right" style={{ opacity: 0.7 }}>
                {msg.createdAt?.toDate ? new Date(msg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {chatGate.disabled ? (
        <div className="p-3.5 border-t border-slate-200 bg-slate-50 text-center text-body text-[0.85rem]">
          🔒 {chatGate.reason}
        </div>
      ) : (
        <div className="p-3 border-t border-slate-200 flex gap-2">
          <input
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            maxLength={500}
            className="flex-1 px-3.5 py-2.5 rounded-full border-[1.5px] border-slate-200 outline-none text-[0.9rem]"
          />
          <button
            onClick={handleSend}
            disabled={sending || !newMsg.trim()}
            className="px-5 py-2.5 rounded-full border-0 cursor-pointer text-white font-semibold"
            style={{ background: newMsg.trim() ? '#2dd4a8' : '#e2e8f0' }}
          >Send</button>
        </div>
      )}
    </div>
  );
}
